// Left-nav availability policy for the two items gated on "does this Mindspace
// run events at all?" — Events and Organisers.
//
// Extracted out of `app-layout.tsx` so the truth table can be asserted without
// standing up PageLayout's whole provider stack. `app-layout.tsx` calls these and
// nothing else; nav-availability.spec.tsx asserts both halves of that.
//
// Both rules are strictly ADDITIVE — a new `&&` term on top of the condition that
// already shipped. Neither one replaces or widens an existing gate, so no
// Mindspace that shows a tab today can start showing one it did not show before.

/** Inputs for the Events tab. */
export interface EventsNavAvailabilityInput {
  /** `Mindspace.modules.events` — the admin-controlled module switch. */
  eventsModuleEnabled: boolean;
  /**
   * `Mindspace.events.hasPublishedEvents` — at least one published-or-completed
   * event THIS VIEWER can open, past OR future. No date filter: a Mindspace whose
   * events are all in the past still keeps its tab, because the archive is the
   * point. Viewer-scoped, so a cohort-only event never lights the tab for someone
   * outside that cohort.
   */
  hasPublishedEvents: boolean;
}

/** Inputs for the Organisers item. */
export interface OrganisersNavAvailabilityInput {
  /** The URL currently being served (the request URL in the root loader). */
  currentUrl: string;
  /** `Config.communityUrl` — the configured Community Mindspace origin. */
  communityUrl: string;
  /** `Config.environment` — "local" | "s101" | "production" | … */
  environment: string;
  /** Same signal as the Events tab. */
  hasPublishedEvents: boolean;
}

/**
 * Events tab: the module must be on AND there must be an event behind the tab
 * FOR THIS VIEWER.
 *
 * The module flag alone left an empty tab on every Mindspace that has events
 * switched on but has never run one — including every self-serve space. The
 * has-events signal was Mindspace-global at first, which left the same empty tab
 * up for a learner whose space runs only cohort-scoped events they are not in; it
 * is viewer-scoped now, matching what `/events` will actually render.
 */
export function isEventsNavAvailable({
  eventsModuleEnabled,
  hasPublishedEvents,
}: EventsNavAvailabilityInput): boolean {
  return eventsModuleEnabled && hasPublishedEvents;
}

/**
 * Organisers item: the pre-existing community-host + non-production gate,
 * unchanged, AND the same has-events signal.
 *
 * The hostname test is deliberately still `includes`, not equality — that is the
 * gate exactly as it shipped, and this change is additive only. (It is a looser
 * test than the /organisers route's own `uniqueName === "community"` check, so a
 * tenant like `mycommunity.s101.…` can match here and then 404 on the route. That
 * mismatch predates this change and is left alone on purpose.)
 */
export function isOrganisersNavAvailable({
  currentUrl,
  communityUrl,
  environment,
  hasPublishedEvents,
}: OrganisersNavAvailabilityInput): boolean {
  const isCommunity = new URL(currentUrl).hostname.includes(
    new URL(communityUrl).hostname,
  );
  const isProduction = environment === "production";
  return isCommunity && !isProduction && hasPublishedEvents;
}
